import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ComposerDialogComponent } from './composer-dialog.component';
import { Composer } from '../../models/composer';
import { ComposerService } from '../../services/composer.service';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { ResponsiveService } from '../../core/responsive.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';


@Component({
  selector: 'app-composers',
  templateUrl: './composers.component.html',
  styleUrls: ['./composers.component.scss'], // Updated to .scss
  standalone: false
})
export class ComposersComponent implements OnInit, OnDestroy {
  @Input() resourceFolder!: string; // Ensure this is a public property

  composers: Composer[] = [];
  filteredComposers: Composer[] = []; // Add a filtered list for search results
  searchQuery: string = ''; // Add a property to hold the search query
  currentUserIsAdmin: boolean = false;
  isListCollapsed: boolean = false; // Add property to manage list collapse
  displayedColumns: string[] = []; // Initialize as empty
  isHandset$!: Observable<boolean>; // Expose for template and logic
  private destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private composerService: ComposerService,
    private authService: AuthService, // Inject AuthService
    private router: Router, // Inject Router
    private responsive: ResponsiveService
  ) {}

  ngOnInit(): void {
    this.loadComposers();
    this.currentUserIsAdmin = this.authService.isCurrentUserAdmin();

    // Set a sane default for initial render (desktop layout) before responsive subscription fires
  const desktopAdminCols = ['surname', 'name', 'songs', 'country', 'yearOfBirth', 'yearOfDeath', 'actions'];
  const desktopUserCols = ['surname', 'name', 'songs', 'country', 'yearOfBirth', 'yearOfDeath'];
    this.displayedColumns = this.currentUserIsAdmin ? desktopAdminCols : desktopUserCols;

    // Wire up handset observable and adapt columns responsively
    this.isHandset$ = this.responsive.isHandset$;
    this.isHandset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isHandset => {
        if (isHandset) {
          // Handset shows only two columns regardless of admin
          this.displayedColumns = ['name', 'songs'];
        } else {
          this.displayedColumns = this.currentUserIsAdmin ? desktopAdminCols : desktopUserCols;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadComposers(): void {
    this.composerService.getComposers().subscribe(data => {
      this.composers = data;
  this.filteredComposers = data; // Initialize filtered list with all composers
  this.sortFilteredComposersBySurname(); // Default sort by first column (Surname)
    });
  }

  searchComposers(): void {
    console.log('Search query:', this.searchQuery); // Debug: Log the search query
    const query = this.searchQuery?.toLowerCase().trim() || '';
    
    this.filteredComposers = this.composers.filter(composer => {
      // Safely handle null values with optional chaining and nullish coalescing
      const fullName = composer.fullName?.toLowerCase() || '';
      const country = composer.countryOfBirth?.toLowerCase() || '';
      const description = composer.descriptionShort?.toLowerCase() || '';
      
      return fullName.includes(query) || 
             country.includes(query) || 
             description.includes(query);
    });
  // Default sort by Surname (first column on desktop)
  this.sortFilteredComposersBySurname();
    console.log('Filtered composers:', this.filteredComposers); // Debug: Log the filtered results
  }

  viewComposer(composer: Composer): void {
    this.dialog.open(ComposerDialogComponent, {
      data: { composer, viewMode: true },
      width: '1000px',
      maxWidth: '90vw'
    });
  }

  editComposer(composer: Composer): void {
    const dialogRef = this.dialog.open(ComposerDialogComponent, {
      data: { composer, viewMode: false },
      width: '1000px',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadComposers();
    });
  }

  addComposer(): void {
    const dialogRef = this.dialog.open(ComposerDialogComponent, {
      data: { composer: null, viewMode: false },
      width: '1000px',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadComposers();
    });
  }

  deleteComposer(composerId: number): void {
    if (confirm('Are you sure you want to delete this composer?')) {
      this.composerService.deleteComposer(composerId).subscribe(() => {
        this.loadComposers();
      });
    }
  }

  toggleListCollapse(): void {
    this.isListCollapsed = !this.isListCollapsed;
  }

  navigateToSongs(composerId: number): void {
    this.router.navigate(['/songs'], { queryParams: { composerId } });
  }

  /** Sort helper: sorts by surname ascending; falls back to fullName; case-insensitive; nulls last */
  private sortFilteredComposersBySurname(): void {
    this.filteredComposers = [...(this.filteredComposers || [])].sort((a, b) => {
      const as = (a?.surname || '').toString().trim();
      const bs = (b?.surname || '').toString().trim();
      // If both surnames present and different, compare surnames
      if (as && bs && as.localeCompare(bs, undefined, { sensitivity: 'base' }) !== 0) {
        return as.localeCompare(bs, undefined, { sensitivity: 'base' });
      }
      // If one surname missing, push missing to end
      if (!as && bs) return 1;
      if (as && !bs) return -1;
      // Surnames equal or both missing: fall back to fullName
      const af = (a?.fullName || '').toString();
      const bf = (b?.fullName || '').toString();
      if (!af && !bf) return 0;
      if (!af) return 1;
      if (!bf) return -1;
      return af.localeCompare(bf, undefined, { sensitivity: 'base' });
    });
  }
}
